import React, { FC, Fragment, useEffect } from 'react';
import { Box, FormControl, Grid, TextField, Typography } from '@mui/material';
import { ContactInformation, LabClient } from 'models/client-directory';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { isValidEmail } from 'utils/validations';

type InvoiceContactInformationProps = {
    labClient?: LabClient;
    invoiceEnabled: boolean;
    setAllInvoiceSettings?: (settings: any) => void;
    onValidityChange?: (isValid: boolean) => void;
};
const InvoiceContactInformation: FC<InvoiceContactInformationProps> = ({
    labClient,
    setAllInvoiceSettings,
    onValidityChange,
}: InvoiceContactInformationProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'organization']);

    const validate = (values: ContactInformation) => {
        const errors: Record<string, string> = {};
        if (values.email && !isValidEmail(values.email)) {
            errors.email = translate('organization:VALIDATION:INVALID_EMAIL');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            email: labClient?.invoiceSettings?.contactInformation?.email || '',
            name: labClient?.invoiceSettings?.contactInformation?.name || '',
            phone: labClient?.invoiceSettings?.contactInformation?.phone || '',
        } as ContactInformation,
        validate,
        // Form is never submitted directly; parent saves via lifted state.
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSubmit: () => {},
    });

    useEffect(() => {
        if (!setAllInvoiceSettings) return;
        setAllInvoiceSettings({ contactInformation: formik.values });
    }, [formik.values]);

    useEffect(() => {
        if (!onValidityChange) return;
        onValidityChange(formik.isValid);
    }, [formik.isValid]);

    return (
        <Fragment>
            <Box mt={3}>
                <Box>
                    <Typography variant="h4" sx={{ marginBottom: 1 }}>
                        {translate('client-directory:CONTACT_INFORMATION')}
                    </Typography>
                    <Grid container spacing={1}>
                        <Grid item sm={4} xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label={translate('common:NAME')}
                                    margin="dense"
                                    {...formik.getFieldProps('name')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={4} xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label={translate('common:EMAIL')}
                                    margin="dense"
                                    type="email"
                                    helperText={formik.touched.email && formik.errors.email}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    {...formik.getFieldProps('email')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={4} xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    margin="dense"
                                    label={translate('common:PHONE')}
                                    {...formik.getFieldProps('phone')}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Fragment>
    );
};

export default InvoiceContactInformation;
